import {
  computeOptionValidity,
  computeOptionsValidity,
} from '../../../../../shared/js/options-utils'
import Button from './Button'
import Checkbox from './Checkbox'
import ImageInput from './ImageInput'
import Input from './Input'
import OptionSummary from './OptionSummary'
import Radios from './Radios'
import Select from './Select'
import TextArea from './TextArea'
import useMediaQuery from '@mui/material/useMediaQuery'
import React, { createElement, useState } from 'react'
import { createPortal } from 'react-dom'
import type {
  ProductOptionValueProps,
  ProductOptionWithNameProps,
} from '../../../../../shared/js/Module'

export interface OptionComponentProps extends ProductOptionWithNameProps {
  // How deep components are imbricated?
  depth?: number
  deleteDisabled?: boolean
  optionsComponents?: { [key: string]: any }
  onCancel?: Function
  SummaryComponent?: any
  // If present: where to render summary outside option component?
  portalId?: string
  [key: string]: any
}

export interface TweakedOption {
  date: Date | string
  options: ProductOptionWithNameProps[]
  userName: string
}

// Option kinds that won‘t need manual validation on option value change/update.
export const autoSubmittingKinds = ['boolean', 'selection']

const optionComponentsMap = {
  button: Button,
  checkbox: Checkbox,
  image: ImageInput,
  input: Input,
  radio: Radios,
  select: Select,
  text: TextArea,
}

// Dynamically load component depending on expected display mode.
// Display/Component mapping is done with `optionsComponents`.
const Option = ({
  depth = 0,
  displayRepresentation,
  onCancel,
  onChange,
  onFontChange,
  optionsComponents = optionComponentsMap,
  portalId,
  deleteDisabled = false,
  restrictionMax,
  restrictionMin,
  SummaryComponent = OptionSummary,
  valid = true,
  ...props
}: OptionComponentProps) => {
  // Adjust component display representation depending on current screen size
  const smallScreen = useMediaQuery('(max-width:480px)')

  const adjustedRepresentation =
    displayRepresentation === 'radio' && smallScreen && depth === 0
      ? 'select'
      : displayRepresentation
  const optionComponent = optionsComponents[adjustedRepresentation]
  const [optionsValidity, setOptionsValidity] = useState<
    Record<string, boolean>
  >({})
  const [fontClassName, setFontClassName] = useState(undefined)
  const [focusableOptionId, setFocusableOptionId] = useState<number | null>(
    null
  )

  if (optionComponent === undefined) {
    return null
  }

  const { values } = props
  // If values are selectable, then load the selected one
  const selectedValue =
    props.kind == 'selection'
      ? values.find(({ selected }) => selected)
      : values[0]
  const options: ProductOptionWithNameProps[] = selectedValue?.options || []

  const optionsAreValid = computeOptionsValidity(options)

  // Group siblings for display purpose
  const groupedOptions = groupSiblingOptions(options)
  const childrenWrapper = depth === 0 ? 'fieldset' : 'div'

  const children = groupedOptions?.map((options, groupIndex) =>
    createElement(
      childrenWrapper,
      { key: groupIndex, className: 'right-action-modal__separator' },
      options.map((option: ProductOptionWithNameProps, optIndex: number) => {
        const nextOption =
          options[optIndex + 1] || groupedOptions[groupIndex + 1]?.[0]
        const selectable = ['select', 'radio'].includes(
          option.displayRepresentation
        )

        return (
          <Option
            {...option}
            depth={depth + 1}
            triggerFocus={focusableOptionId === option.id}
            key={option.id}
            fontClassName={fontClassName}
            name={`${option.displayRepresentation}-${option.id}`}
            onChange={(id: number, values: ProductOptionValueProps[]) => {
              handleChildChange(id, values)
              selectable && setFocusableOptionId(nextOption?.id)
            }}
            onFontChange={setFontClassName}
            optionsComponents={optionsComponents}
            valid={
              optionsValidity[option.id] === undefined ||
              optionsValidity[option.id]
            }
          />
        )
      })
    )
  )

  const summaryComponent = (
    <SummaryComponent deleteDisabled onCancel={onCancel} {...props} />
  )

  const renderSummary = () => {
    if (typeof document === 'undefined' || !portalId) {
      return summaryComponent
    }

    const summaryPortalNode = document.getElementById(portalId)

    return summaryPortalNode
      ? createPortal(summaryComponent, summaryPortalNode)
      : summaryComponent
  }

  return (
    <>
      {renderSummary()}
      {createElement(
        optionComponent,
        {
          ...props,
          name: `${displayRepresentation}-${props.id}`,
          onCancel,
          onChange,
          onFontChange,
          restrictionMax,
          restrictionMin,
          // When options exist, mix options validity with current option validity
          valid: options.length ? optionsAreValid && valid : valid,
        },
        children
      )}
    </>
  )

  // Manage children updates transfer to `onchange` callback.
  function handleChildChange(
    optionId: number,
    optValues: ProductOptionValueProps[]
  ) {
    const updatedOptions = options?.map((option: ProductOptionWithNameProps) =>
      option.id !== optionId ? option : { ...option, values: optValues }
    )
    // Prevent options loss (children calling "handleChange" only pass there own values)
    const updatedValues = values.map((value) =>
      value.id === selectedValue?.id
        ? { ...value, options: updatedOptions }
        : value
    )
    onChange?.(props.id, updatedValues)

    // Check if current option is valid after change
    const updatedOption = updatedOptions.find(({ id }) => id === optionId)

    if (!updatedOption) return

    setOptionsValidity({
      ...optionsValidity,
      [optionId]: computeOptionValidity(updatedOption),
    })
  }
}

// When many options with the same display representation follow each other then
// group them (for display purpose).
function groupSiblingOptions(
  options: ProductOptionWithNameProps[]
): Array<ProductOptionWithNameProps[]> {
  const groupedOptions: Array<ProductOptionWithNameProps[]> = []
  let previousDisplayRep

  for (const option of options) {
    if (previousDisplayRep === option.displayRepresentation) {
      const lastGroup = groupedOptions.pop() || []
      groupedOptions.push([...lastGroup, option])
    } else {
      groupedOptions.push([option])
    }
    previousDisplayRep = option.displayRepresentation
  }

  return groupedOptions
}

export default Option
